// HOME BOARD MOTION — cards move instead of jumping (Nika 2026-09-28: "also
// add transitions and animations on moving around the widgets";
// DESIGN-WL-HOME-WIDTH-RESIZE-1, HOME_WIDGETS_SPEC §1 dated line).
//
// ONE technique for every change of the board's arrangement — a reorder (drag
// or keys), a resize (handle, keys or the picker), a restore and a hide: FLIP.
//   First   — read every card's rect before the change (`homeWidgetFlipCapture`);
//   Last    — apply the change, then read the rects again;
//   Invert  — put each moved card back where it was with an inline `translate`
//             (the individual CSS property, so it never fights the drag's own
//             pointer-follow `transform`), transitions off;
//   Play    — hand the card to its declared transition: the frame
//             (components/HomeWidgetFrame.js) lists `translate C defaultBezier`,
//             so clearing the inline value glides it home.
// A card that GREW is revealed with an `inset()` clip from its old box to its
// new one (`clip-path C defaultBezier`) — its content is already laid out at
// the new size, so nothing stretches. A hidden card leaves with the brand's
// `modalOutDown` (fade + a short fall, `B`); a restored card enters with the
// frame's own `fadeInUpShort`.
//
// The motion is CSS-driven: the inline values are the START of a declared
// transition, and the layout underneath is already final. So the state after a
// move is correct without a single frame — a hidden tab or a screenshot taken
// mid-way can only show the card on its way, never in a wrong cell.
//
// NO MOTION — the change lands at once — when (`homeWidgetMotionAllowed`):
//   • the OS asks for reduced motion (`prefers-reduced-motion: reduce`);
//   • the person asked for it in the app (`root.a11yMotion`);
//   • the document is not visible (the PageSlot / routeTransition rule:
//     transitions are frame-gated, and animating for nobody is pure risk).
//
// `this` is an element (el.call). Every export stands alone (frank serializes
// each one by itself): helpers are local consts or registered functions reached
// through `this.call`. Styles go through `setNodeStyles`, the element's
// sanctioned inline-style escape hatch (the drag's own channel); reads are
// `getBoundingClientRect` / `offsetWidth` on nodes the grid already owns.

// May the board animate right now? `node` names the document to ask (the
// grid's own node); without one, the element's.
export function homeWidgetMotionAllowed(node) {
  const el = this
  const own = el && el.node
  const doc = (node && node.ownerDocument) || (own && own.ownerDocument) || null
  if (!doc || doc.visibilityState !== 'visible') return false
  const win = doc.defaultView
  if (
    win &&
    typeof win.matchMedia === 'function' &&
    win.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return false
  }
  const st = el && el.state
  const root = (st && st.root) || st
  if (root && root.a11yMotion) return false
  return true
}

// The FLIP math: the inverse of the move from rect `first` to rect `last`.
// `dx` / `dy` put the card back at its first place; `sx` / `sy` are the classic
// inverse scale (first / last), kept for callers that want it; `grow` is how
// much wider / taller the card got (0 when it shrank) — the clip reveal's start
// insets. `moved` / `resized` ignore sub-half-pixel noise. null for an empty
// or missing rect (a card hidden before or after the change has no motion).
export function homeWidgetFlipDelta(first, last) {
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
  if (!first || !last) return null
  const fw = num(first.width)
  const fh = num(first.height)
  const lw = num(last.width)
  const lh = num(last.height)
  if (!(fw > 0) || !(fh > 0) || !(lw > 0) || !(lh > 0)) return null
  const round = (v, p) => Math.round(v * p) / p
  const dx = round(num(first.left) - num(last.left), 100)
  const dy = round(num(first.top) - num(last.top), 100)
  const growRight = Math.max(0, round(lw - fw, 100))
  const growBottom = Math.max(0, round(lh - fh, 100))
  return {
    dx,
    dy,
    sx: round(fw / lw, 10000),
    sy: round(fh / lh, 10000),
    grow: { right: growRight, bottom: growBottom },
    moved: Math.abs(dx) >= 0.5 || Math.abs(dy) >= 0.5,
    resized: Math.abs(lw - fw) >= 0.5 || Math.abs(lh - fh) >= 0.5
  }
}

// The START styles of a card's glide, from its delta: the inline `translate`
// that puts it back where it was, and — for a card that grew — the clip that
// shows only its old box (`radius` = the card's own corner, read from the
// node). null when there is nothing to animate.
export function homeWidgetFlipStart(delta, radius) {
  if (!delta) return null
  const growing = delta.grow && (delta.grow.right >= 0.5 || delta.grow.bottom >= 0.5)
  if (!delta.moved && !growing) return null
  const out = {}
  if (delta.moved) out.translate = `${delta.dx}px ${delta.dy}px`
  if (growing) {
    const r = radius ? ` round ${radius}` : ''
    out.clipPath = `inset(0px ${delta.grow.right}px ${delta.grow.bottom}px 0px${r})`
    out.clipPathEnd = `inset(0px 0px 0px 0px${r})`
  }
  return out
}

// FIRST: every visible card's rect, keyed by its widget id — as it is SEEN
// (a card still gliding from an earlier move is read where it is on screen,
// so a second move continues from there instead of snapping).
export function homeWidgetFlipCapture(grid) {
  const node = grid && (grid.node || grid)
  const kids = node && node.children
  const out = {}
  if (!kids) return out
  Array.prototype.forEach.call(kids, (child) => {
    const id = child && child.getAttribute && child.getAttribute('data-widget-id')
    if (!id || typeof child.getBoundingClientRect !== 'function') return
    const r = child.getBoundingClientRect()
    if (!r || !(r.width > 0) || !(r.height > 0)) return
    out[id] = { left: r.left, top: r.top, width: r.width, height: r.height }
  })
  return out
}

// LAST + INVERT + PLAY for every card of `grid` that `first` saw. Returns how
// many cards glide (0 = the change landed at once). `opts.exclude` = ids that
// own their motion (the card under the pointer); `opts.resized` = the id whose
// size changed (the only card that may be revealed by a clip).
export function homeWidgetFlipPlay(grid, first, opts) {
  const node = grid && (grid.node || grid)
  const kids = node && node.children
  if (!kids || !first) return 0
  if (!this.call('homeWidgetMotionAllowed', node)) return 0
  const skip = new Set((opts && opts.exclude) || [])
  const resized = opts && opts.resized
  const cards = []
  Array.prototype.forEach.call(kids, (child) => {
    const id = child && child.getAttribute && child.getAttribute('data-widget-id')
    const card = child && child.ref
    if (!id || skip.has(id) || !first[id] || !card || typeof card.setNodeStyles !== 'function')
      return
    cards.push({ id, child, card })
  })
  if (!cards.length) return 0
  // 1. Cancel whatever glide is in flight, so LAST is the true layout.
  for (const c of cards) {
    if (c.card.__homeClipTimer != null) clearTimeout(c.card.__homeClipTimer)
    c.card.__homeClipTimer = null
    c.card.setNodeStyles({ transition: 'none', translate: '0 0', clipPath: '' })
  }
  // 2. Read every LAST rect in one pass (one layout), then write the inverses.
  const plans = []
  const planned = new Set()
  for (const c of cards) {
    const r = c.child.getBoundingClientRect()
    const delta = this.call('homeWidgetFlipDelta', first[c.id], r)
    if (!delta) continue
    let radius = null
    if (c.id === resized && delta.grow && (delta.grow.right >= 0.5 || delta.grow.bottom >= 0.5)) {
      const win = c.child.ownerDocument && c.child.ownerDocument.defaultView
      const cs =
        win && typeof win.getComputedStyle === 'function' ? win.getComputedStyle(c.child) : null
      radius = (cs && cs.borderTopLeftRadius) || null
    }
    const start = this.call(
      'homeWidgetFlipStart',
      c.id === resized ? delta : { ...delta, grow: { right: 0, bottom: 0 } },
      radius
    )
    if (start) {
      plans.push({ ...c, start })
      planned.add(c.id)
    }
  }
  for (const c of cards) {
    if (!planned.has(c.id)) c.card.setNodeStyles({ transition: '', translate: '' })
  }
  if (!plans.length) return 0
  for (const p of plans) {
    p.card.setNodeStyles({
      translate: p.start.translate || '0 0',
      clipPath: p.start.clipPath || ''
    })
  }
  // 3. Commit the start frame (a layout read), then PLAY: back to the declared
  //    transition, the inline start values cleared (the clip goes to its full
  //    box, and is dropped once the glide has had its time).
  void node.offsetWidth
  let clipLeft = 0
  for (const p of plans) {
    p.card.setNodeStyles({ transition: '', translate: '' })
    if (p.start.clipPathEnd) {
      p.card.setNodeStyles({ clipPath: p.start.clipPathEnd })
      clipLeft++
    }
  }
  if (clipLeft) {
    const ds = this.context && this.context.designSystem
    const step = ds && ds.timing && ds.timing.sequence && ds.timing.sequence.C
    const wait = (step && Number(step.val)) || 0
    for (const p of plans) {
      if (!p.start.clipPathEnd) continue
      const card = p.card
      const clear = () => {
        card.__homeClipTimer = null
        card.setNodeStyles({ clipPath: '' })
      }
      if (wait > 0 && typeof setTimeout === 'function') {
        card.__homeClipTimer = setTimeout(clear, wait + 60)
      } else clear()
    }
  }
  return plans.length
}

// The one entry point: run `change` (a function that applies the new layout /
// hidden map) between FIRST and PLAY. Returns whatever `change` returned.
//
// PLAY WAITS FOR THE FLUSH. smbls applies a state update to the DOM in its
// effect flush — ONE MICROTASK after `root.update` (measured live on dev
// a9783341a, 2026-09-29: the card still read its old size synchronously and
// its new one after `await Promise.resolve()`). Read synchronously, LAST was
// still FIRST: no card glided, and the reveal scrolled by the card's OLD box
// (247 px for a card that had moved a row down). So the play is queued as a
// microtask AFTER the change: the flush's own microtask was queued first by
// the change's update, so it has run by then (a flush that is already
// draining finishes its cascade inside its own microtask) — and no frame can
// paint between the two, so nothing flashes. `opts.after(played)` runs once
// the play is done (the reveal reads the final layout there).
export function homeWidgetFlip(grid, change, opts) {
  const node = grid && (grid.node || grid)
  const first = this.call('homeWidgetMotionAllowed', node)
    ? this.call('homeWidgetFlipCapture', grid)
    : null
  const result = typeof change === 'function' ? change() : undefined
  const el = this
  const play = () => {
    const played = el.call('homeWidgetFlipPlay', grid, first, opts)
    if (opts && typeof opts.after === 'function') opts.after(played)
  }
  if (typeof queueMicrotask === 'function') queueMicrotask(play)
  else Promise.resolve().then(play)
  return result
}

// A card LEAVES (hide): the brand's `modalOutDown` (fade + a short fall) at `B`
// on `defaultBezier`, then `done()` — which applies the hide (and reflows the
// neighbours through `homeWidgetFlip`). Without motion, or without the tokens,
// or for a card that is not on the board, `done()` runs at once. The fill holds
// the last frame until the hide lands, and is then cancelled so a later restore
// never starts invisible. A timer backs up the finish event.
export function homeWidgetExit(grid, id, done) {
  const finish = typeof done === 'function' ? done : () => {}
  const node = grid && (grid.node || grid)
  const kids = node && node.children
  let card = null
  if (kids && id) {
    Array.prototype.forEach.call(kids, (child) => {
      if (!card && child && child.getAttribute && child.getAttribute('data-widget-id') === id)
        card = child
    })
  }
  if (!card || typeof card.animate !== 'function' || !this.call('homeWidgetMotionAllowed', node)) {
    return finish()
  }
  const r = card.getBoundingClientRect()
  if (!r || !(r.width > 0)) return finish()
  const ds = this.context && this.context.designSystem
  const timing = ds && ds.timing
  const step = timing && timing.sequence && timing.sequence.B
  const duration = step && Number(step.val)
  const easing = timing && timing.defaultBezier
  const frames = ds && ds.animation && ds.animation.modalOutDown
  if (!duration || !easing || !frames || !frames.to) return finish()
  const from = { opacity: 1 }
  const anim = card.animate([from, frames.to], { duration, easing, fill: 'forwards' })
  anim.id = 'homeWidgetExit'
  let ran = false
  let timer = null
  const land = () => {
    if (ran) return
    ran = true
    if (timer != null) clearTimeout(timer)
    timer = null
    try {
      finish()
    } finally {
      try {
        anim.cancel()
      } catch (_) {
        /* already gone with its card */
      }
    }
  }
  Promise.resolve(anim.finished).then(land, land)
  if (typeof setTimeout === 'function') timer = setTimeout(land, duration + 120)
  return undefined
}

// After a resize lands, the resized card is brought into view (Nika
// 2026-09-28): the page scrolls the LEAST it can ("nearest") so the whole
// card shows between the column's own insets (its top padding clears the
// floating header, its bottom padding the composer). Nothing scrolls when the
// card is already fully in view; the scroll is smooth where motion is allowed
// and instant under reduced motion. The card's box is read from its LAYOUT
// offsets, never its transformed rect — its FLIP glide may still be in
// flight. `column` = the scroll container (the board's MainColumn), `grid` =
// the card grid. Returns 'visible' | 'smooth' | 'instant' | 'none'.
export function homeWidgetReveal(column, grid, id) {
  const scroller = column && (column.node || column)
  const node = grid && (grid.node || grid)
  const kids = node && node.children
  if (!scroller || !kids || !id || typeof scroller.scrollBy !== 'function') return 'none'
  let card = null
  Array.prototype.forEach.call(kids, (child) => {
    if (!card && child && child.getAttribute && child.getAttribute('data-widget-id') === id)
      card = child
  })
  if (!card || !(card.offsetHeight > 0) || typeof node.getBoundingClientRect !== 'function')
    return 'none'
  const g = node.getBoundingClientRect()
  const top = g.top + card.offsetTop
  const bottom = top + card.offsetHeight
  const view = scroller.getBoundingClientRect()
  const win = scroller.ownerDocument && scroller.ownerDocument.defaultView
  const cs =
    win && typeof win.getComputedStyle === 'function' ? win.getComputedStyle(scroller) : null
  const from = view.top + ((cs && parseFloat(cs.paddingTop)) || 0)
  const to = view.bottom - ((cs && parseFloat(cs.paddingBottom)) || 0)
  if (top >= from - 0.5 && bottom <= to + 0.5) return 'visible'
  // a card taller than the room, or above it, aligns its top; else its bottom
  const delta = bottom - top > to - from || top < from ? top - from : bottom - to
  const smooth = !!this.call('homeWidgetMotionAllowed', scroller)
  scroller.scrollBy({ top: Math.round(delta), behavior: smooth ? 'smooth' : 'auto' })
  return smooth ? 'smooth' : 'instant'
}
